import React, { useState, useCallback } from 'react';
import { ApolloQueryResult } from 'apollo-client-ppc';
import isEqual from 'lodash/isEqual';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import { token } from '@atlaskit/tokens';
import { P200 } from '@atlaskit/theme/colors';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { Field, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { Checkbox } from '@atlaskit/checkbox';
import Button from '@atlaskit/button';
import { SelectShipToAddress, shipToAddressesType } from '@css-xp/base';
import InfoIcon from '@atlaskit/icon/glyph/info';

import CountryData from 'country-data';
import { FormatCommerceAddress, Country, EditSoldToAddressDisabledMessage } from '@css-xp/ppc-common';
// @ts-ignore
import LockIcon from '@css-xp/base/assets/img/icon-lock.svg';
import styled from '@emotion/styled';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { SoldToAddressDetailsWrapper } from './sold-to-address-details-wrapper';
import './sold-to-address-section.scss';

export interface FormData {
  soldToName?: string | null;
  soldToAddress1?: string | null;
  soldToAddress2?: string | null;
  soldToCity?: string | null;
  soldToCountry?: { value: string; label: string };
  soldToPostalCode?: string | null;
  soldToState?: { value: string; label: string };
  soldToTaxId?: string | null;
  soldToGst?: string | null;
  soldToPst?: string | null;
  currency?: string;
  recipients?: string[];
  invoiceGroupName?: string;
  purchaseOrderNumber?: string | null;
  defaultPaymentMethod?: string;
}

const InlineDialog = styled(TooltipPrimitive)`
  background: white;
  border-radius: ${token('border.radius', '4px')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-sizing: content-box;
  padding: ${token('space.100', '8px')} ${token('space.150', '12px')};
  max-width: 330px;
`;

interface SoldToAddressSectionProps {
  transactionAccountData:
    | ApolloQueryResult<generatedTypesNew.FetchTransactionAccountForEditBillingProfilePaneQuery>['data']
    | undefined;
  shipToAddress: generatedTypesNew.CommerceAddress | undefined | null;
  onShipToAddressUpdate: (_newAddress: generatedTypesNew.CommerceAddress | null | undefined) => void;
  billingProfileData: FormData;
  isMultiCurrencyChangesEnabled?: boolean;
  isNewSoldToAddressDisabled?: boolean;
  isBillingProfileUsedForInternalUsageLicense: boolean;
}

export const SoldToAddressSection = ({
  transactionAccountData,
  shipToAddress,
  onShipToAddressUpdate,
  billingProfileData,
  isMultiCurrencyChangesEnabled,
  isNewSoldToAddressDisabled = false,
  isBillingProfileUsedForInternalUsageLicense,
}: SoldToAddressSectionProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const [newSoldToAddress, setNewSoldToAddress] = useState<generatedTypesNew.CommerceAddress | null>();
  const billToPartyData = transactionAccountData?.transactionAccount?.billToParty;
  // FIXME :: Make it generic for all country
  // 1. If ship-to address  is of JP, we will disable if bill-to is of non-JP
  // 2. If ship-to address is of non-JP, we will disable if bill-to is of JP
  const isBillToAndShipToAddressOfDifferentCurrency =
    (isMultiCurrencyChangesEnabled &&
      shipToAddress?.country?.toLowerCase() === Country.JP.toLowerCase() &&
      billToPartyData?.country?.toLowerCase() !== Country.JP.toLowerCase()) ||
    (shipToAddress?.country?.toLowerCase() !== Country.JP.toLowerCase() &&
      billToPartyData?.country?.toLowerCase() === Country.JP.toLowerCase());

  const onSameAsBillTo = useCallback(() => {
    // check if available ship to addresses already have a address similar to bill to address
    const billToParty = {
      name: billToPartyData?.name,
      taxId: billToPartyData?.taxId || null,
      taxIds: billToPartyData?.taxIds?.length ? billToPartyData?.taxIds : null,
      postalAddress: {
        line1: billToPartyData?.addressLine1,
        line2: billToPartyData?.addressLine2,
        city: billToPartyData?.city,
        country: billToPartyData?.country,
        state: billToPartyData?.state,
        postcode: billToPartyData?.postcode,
      },
    };
    const hasSameShipToParty = transactionAccountData?.transactionAccount.availableShipToParties?.nodes?.find(
      (node) => {
        const shipToParty = {
          name: node?.name,
          taxId: node?.taxId || null,
          taxIds: node?.taxIds?.length ? node?.taxIds : null,
          postalAddress: {
            line1: node?.addressLine1,
            line2: node?.addressLine2,
            city: node?.city,
            country: node?.country,
            state: node?.state,
            postcode: node?.postcode,
          },
        };

        return isEqual(shipToParty, billToParty);
      }
    );

    const newSelectedSoldToAddress = isChecked ? null : hasSameShipToParty ? hasSameShipToParty : billToPartyData;

    // set new sold-to address selected from sold-to list dropdown
    setNewSoldToAddress(newSelectedSoldToAddress);
    onShipToAddressUpdate(newSelectedSoldToAddress);

    setIsChecked((current) => !current);
  }, [billToPartyData, isChecked, onShipToAddressUpdate, transactionAccountData]);

  const onSelect = useCallback(
    (selected: any) => {
      // uncheck the same as billing address checkbox first in case the address selected from dropdown
      setIsChecked(false);

      // set new sold-to address selected from sold-to list dropdown and update the parent component
      setNewSoldToAddress(selected?.value);
      onShipToAddressUpdate(selected?.value);
    },
    [onShipToAddressUpdate]
  );

  return (
    <>
      <FormSection>
        <div className="form-component-width">
          <h3>{billingProfileData.invoiceGroupName}</h3>

          <Field
            name="invoiceGroupName"
            id="invoiceGroupName"
            defaultValue={billingProfileData.invoiceGroupName}
            label="Name"
            isRequired={true}
          >
            {({ fieldProps }) => <Textfield {...fieldProps} name="invoiceGroupName" testId="billing-profile-name" />}
          </Field>
        </div>
      </FormSection>
      <div className="form-component-width commerce-address-container">
        <h3 className="sold-to-address-header">Current sold-to address</h3>
        <SoldToAddressDetailsWrapper
          shipToAddress={shipToAddress}
          isBillingProfileUsedForInternalUsageLicense={isBillingProfileUsedForInternalUsageLicense}
        >
          <div className="selected-commerce-address-container">
            <FormatCommerceAddress
              commerceAddress={shipToAddress}
              showCurrency={isMultiCurrencyChangesEnabled}
              showPricingEligibility={isMultiCurrencyChangesEnabled}
            />
          </div>
        </SoldToAddressDetailsWrapper>
      </div>
      {!isBillingProfileUsedForInternalUsageLicense && (
        <FormSection>
          <div className="form-component-width commerce-address-container" data-testid="ppc-commerce-address-container">
            <h3 className="sold-to-address-header">
              New Sold-to address{' '}
              {isNewSoldToAddressDisabled && (
                <Tooltip
                  testId="sold-to-address-edit-disabled-tooltip"
                  component={InlineDialog}
                  content={<EditSoldToAddressDisabledMessage />}
                >
                  {/* @ts-ignore */}
                  <img src={LockIcon} data-testid="lock-icon" />
                </Tooltip>
              )}
            </h3>
            {newSoldToAddress ? (
              <div className="selected-commerce-address-container new-address-section">
                <FormatCommerceAddress
                  commerceAddress={newSoldToAddress}
                  showCurrency={isMultiCurrencyChangesEnabled}
                  showPricingEligibility={isMultiCurrencyChangesEnabled}
                />
                <Button
                  appearance="link"
                  testId="ppc-change-sold-to-address"
                  onClick={() => {
                    setNewSoldToAddress(null);
                    setIsChecked(false);
                  }}
                >
                  Change
                </Button>
              </div>
            ) : (
              <>
                <div className="same-as-bill-to-checkbox flex align-center">
                  <Checkbox
                    isChecked={isChecked}
                    onChange={onSameAsBillTo}
                    label="Same as billing address"
                    value="Controlled Checkbox"
                    name="controlled-checkbox"
                    data-testid="same-as-bill"
                    isDisabled={isNewSoldToAddressDisabled || isBillToAndShipToAddressOfDifferentCurrency}
                  />
                  {isBillToAndShipToAddressOfDifferentCurrency && !isNewSoldToAddressDisabled ? (
                    <Tooltip content={<>Billing address is on different currency</>} tag="span">
                      <InfoIcon label="Info" size="small" />
                    </Tooltip>
                  ) : null}
                  <span></span>
                </div>
                <Field
                  label="Or select an existing sold-to address"
                  name="billingFrequency"
                  data-testid="select-soldto-field"
                >
                  {() => (
                    <SelectShipToAddress
                      shipToAddresses={transactionAccountData as shipToAddressesType}
                      onSelect={onSelect}
                      countryCode={shipToAddress?.country}
                      isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
                      isNewSoldToAddressDisabled={isNewSoldToAddressDisabled}
                    />
                  )}
                </Field>
                {isMultiCurrencyChangesEnabled && shipToAddress?.country === Country.JP && (
                  <div className="currency-warning">
                    <EditorInfoIcon label="" size="medium" primaryColor={token('color.icon', P200)} />
                    <div className="warning-message" data-testId="warning-message">
                      The address must be of {CountryData.countries[shipToAddress?.country].name} as your currency is
                      set to JPY.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </FormSection>
      )}
    </>
  );
};
